import { useEffect, useState, useRef } from "react";
import api, { errText } from "@/lib/api";
import { toast } from "sonner";
import { BookText, Plus, Trash2, Upload, Loader2, FileText } from "lucide-react";

const TYPES = [
  ["company", "Company info"],
  ["pitch", "Pitch"],
  ["offer", "Offer / pricing"],
  ["email", "Sample email"],
  ["other", "Other"],
];

export default function Resources() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", type: "pitch", content: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/resources").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error("Title and content are required"); return; }
    setSaving(true);
    try { await api.post("/resources", form); toast.success("Resource added"); setForm({ title: "", type: "pitch", content: "" }); load(); }
    catch (e) { toast.error(errText(e)); } finally { setSaving(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", file.name);
    fd.append("type", "other");
    try { await api.post("/resources/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("File extracted & saved"); load(); }
    catch (e2) { toast.error(errText(e2)); } finally { setUploading(false); e.target.value = ""; }
  };

  const del = async (r) => {
    try { await api.delete(`/resources/${r.id}`); load(); toast.success("Deleted"); } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="font-head text-2xl font-black tracking-tight flex items-center gap-2"><BookText className="h-6 w-6" /> AI Resource Library</h1>
      <p className="text-xs text-zinc-500 font-mono mb-5">Company info, offers, pitch and sample emails — the AI Composer uses these as context so every draft stays on-message</p>

      <div className="grid lg:grid-cols-2 gap-5 mb-6">
        <div className="bg-white border-2 border-black rounded-xl p-4">
          <div className="text-xs font-bold uppercase tracking-wider mb-3">Add a resource</div>
          <input data-testid="resource-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title (e.g. Standard package offer)"
            className="w-full border border-zinc-300 rounded-lg px-2.5 py-2 text-sm mb-2 focus:border-klein focus:outline-none" />
          <select data-testid="resource-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
            className="w-full border border-zinc-300 rounded-lg px-2.5 py-2 text-sm mb-2 focus:border-klein focus:outline-none">
            {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <textarea data-testid="resource-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={6}
            placeholder="Paste the content — current pitch, offer details, pricing, a sample email, company boilerplate…"
            className="w-full border border-zinc-300 rounded-lg p-2.5 text-sm resize-none focus:border-klein focus:outline-none" />
          <button data-testid="add-resource-btn" onClick={add} disabled={saving}
            className="mt-2 flex items-center gap-2 bg-klein hover:bg-klein-hover text-white px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add resource
          </button>
        </div>

        <div className="bg-white border border-zinc-300 rounded-xl p-4 flex flex-col">
          <div className="text-xs font-bold uppercase tracking-wider mb-3">Or upload a file</div>
          <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" onChange={upload} className="hidden" data-testid="resource-file-input" />
          <button data-testid="resource-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="flex-1 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-zinc-400 hover:border-klein rounded-lg py-8 text-sm font-medium transition-colors">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
            {uploading ? "Extracting text…" : "Upload PDF, DOCX or TXT"}
          </button>
          <p className="text-[11px] text-zinc-400 mt-2">We extract the text automatically and store it as context for the AI.</p>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden" data-testid="resource-list">
        {items.map((r) => (
          <div key={r.id} className="px-4 py-3 border-b border-zinc-100 last:border-0" data-testid={`resource-${r.id}`}>
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-zinc-400 shrink-0" />
              <span className="font-medium text-sm">{r.title}</span>
              <span className="text-[10px] font-mono uppercase bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded">{r.type}</span>
              {r.source_filename && <span className="text-[10px] text-zinc-400 font-mono">· {r.source_filename}</span>}
              <button data-testid={`delete-resource-${r.id}`} onClick={() => del(r)} className="ml-auto text-zinc-400 hover:text-red-600 transition-colors"><Trash2 className="h-4 w-4" /></button>
            </div>
            <p className="text-xs text-zinc-500 mt-1 line-clamp-2 pl-6">{r.content}</p>
          </div>
        ))}
        {items.length === 0 && <div className="text-center text-zinc-400 font-mono py-12 text-xs">No resources yet. Add your pitch, offers and sample emails above.</div>}
      </div>
    </div>
  );
}
